/**
 * Déplacement d'une ligne, et d'un bloc de lignes, dans une liste groupée par
 * sections (US-77).
 *
 * ## Pourquoi une fonction partagée
 *
 * L'écran calcule le nouvel ordre pour l'afficher tout de suite, le serveur le
 * reçoit tout entier et renumérote `position`. Les deux doivent tomber sur la
 * même liste, sans quoi le rendu optimiste et la base divergent au premier
 * déplacement qui traverse une frontière de phase. Une seule fonction, dans
 * `shared/`, testée une fois — même raison que `collectionMatch.ts`.
 *
 * Les boutons « monter » / « descendre » et le glisser-déposer appellent
 * **exactement** cette fonction (D9) : le glisser ne décide de rien, il fournit
 * un indice d'arrivée.
 *
 * ## L'invariant, et c'est lui qui fait toute la difficulté
 *
 * Une phase n'est pas une ligne. Il n'existe aucune ligne « titre » : une phase
 * est le `group_label` que portent des lignes **consécutives**, et le titre
 * s'affiche sur la première d'entre elles (`ouvreUneSection`,
 * `ingredientGroups.ts`). D'où l'invariant que tout déplacement doit tenir :
 *
 *   **deux lignes portant la même phase sont toujours contiguës.**
 *
 * L'enfreindre ne lève rien et ne casse rien tout de suite : la fiche affiche
 * simplement « Pour la crème » deux fois, à deux endroits, et la recette ment.
 * (Côté écran, c'est aussi une collision de clé React, `RecipeDetail.tsx` :
 * `groups.map` est indexé par le libellé.)
 *
 * ## La règle de phase, en une phrase
 *
 * **Une étape prend la phase de la ligne sous laquelle elle atterrit** — celle
 * qui la précède désormais. En tête de liste, elle sort de toute phase, sauf
 * si la ligne qui la suit porte déjà la sienne : sans cette exception, monter
 * la deuxième étape d'une recette à une seule phase retirerait silencieusement
 * cette phase.
 *
 * Cette règle suffit à tenir l'invariant sans le vérifier : une ligne insérée
 * avec le libellé de sa voisine du dessus est, par construction, collée au bloc
 * qui le porte.
 */

import type { LigneGroupable } from './ingredientGroups';

/** Ramène un indice dans les bornes de la liste. */
function borner(index: number, taille: number): number {
  if (index < 0) return 0;
  if (index > taille - 1) return Math.max(taille - 1, 0);
  return index;
}

/**
 * Déplace la ligne d'indice `depuis` à l'indice `vers` (indices dans la liste
 * **résultante**), et lui applique la règle de phase ci-dessus.
 *
 * Hors bornes ou sans effet : la liste est rendue telle quelle, copiée. Une
 * liste d'un seul élément est donc un cas normal, pas une erreur.
 */
export function deplacerLigne<T extends LigneGroupable>(
  lignes: readonly T[],
  depuis: number,
  vers: number,
): T[] {
  if (depuis < 0 || depuis >= lignes.length) return [...lignes];
  const cible = lignes[depuis];
  if (cible === undefined) return [...lignes];

  const arrivee = borner(vers, lignes.length);
  if (arrivee === depuis) return [...lignes];

  const sortie = lignes.filter((_, i) => i !== depuis);
  sortie.splice(arrivee, 0, cible);

  const precedente = arrivee === 0 ? undefined : sortie[arrivee - 1];
  const suivante = sortie[arrivee + 1];
  const phase = precedente !== undefined
    ? precedente.group_label
    // En tête : hors de toute phase, sauf si la ligne d'après porte déjà la
    // sienne — auquel cas elle reste en tête de son propre bloc.
    : (suivante?.group_label === cible.group_label ? cible.group_label : null);

  sortie[arrivee] = { ...cible, group_label: phase };
  return sortie;
}

export interface Section {
  /** Libellé de la phase, ou `null` pour les lignes qui n'en portent aucune. */
  label: string | null;
  /** Premier indice de la section. */
  debut: number;
  /** Nombre de lignes. */
  taille: number;
}

/**
 * Découpe la liste en blocs consécutifs de même phase. Les lignes sans phase
 * forment des blocs comme les autres : elles se déplacent aussi.
 */
export function sectionsDe(lignes: readonly LigneGroupable[]): Section[] {
  const sections: Section[] = [];
  for (const [index, ligne] of lignes.entries()) {
    const derniere = sections.at(-1);
    if (derniere !== undefined && derniere.label === ligne.group_label) {
      derniere.taille += 1;
    } else {
      sections.push({ label: ligne.group_label, debut: index, taille: 1 });
    }
  }
  return sections;
}

/** Indice, dans `sectionsDe(lignes)`, de la section qui contient `index`. */
export function sectionDe(lignes: readonly LigneGroupable[], index: number): number {
  return sectionsDe(lignes).findIndex((s) => index >= s.debut && index < s.debut + s.taille);
}

/**
 * Déplace **le bloc entier** auquel appartient la ligne d'indice `index`, d'une
 * section vers le haut (`-1`) ou vers le bas (`+1`).
 *
 * C'est la moitié « un intitulé de phase emmène son bloc » d'US-77. Déplacer le
 * titre seul laisserait les étapes de la phase sous la phase précédente : une
 * façon silencieuse de changer une recette.
 *
 * Aucun `group_label` ne change : on échange deux blocs voisins, et deux blocs
 * voisins qui se retrouveraient de même libellé n'en forment qu'un — donc
 * toujours contigus.
 */
export function deplacerSection<T extends LigneGroupable>(
  lignes: readonly T[],
  index: number,
  sens: -1 | 1,
): T[] {
  const sections = sectionsDe(lignes);
  const rang = sections.findIndex((s) => index >= s.debut && index < s.debut + s.taille);
  if (rang < 0) return [...lignes];

  const voisin = sections[rang + sens];
  const courante = sections[rang];
  if (voisin === undefined || courante === undefined) return [...lignes];

  const premiere = sens === -1 ? voisin : courante;
  const seconde = sens === -1 ? courante : voisin;
  const avant = lignes.slice(0, premiere.debut);
  const apres = lignes.slice(seconde.debut + seconde.taille);
  const blocPremier = lignes.slice(premiere.debut, premiere.debut + premiere.taille);
  const blocSecond = lignes.slice(seconde.debut, seconde.debut + seconde.taille);

  return [...avant, ...blocSecond, ...blocPremier, ...apres];
}
